import { Flex } from 'reflexbox';
import styled, { useTheme } from 'styled-components';

const Label = styled(Flex)`
	font-size: 12px;
	color: ${(props) => props.theme.colors.slateBlue};
	line-height: 16px;
	height: 32px;
	justify-content: center;
	text-align: center;
`;

function fractionToXY(value: number): { x: number; y: number } {
	return {
		x: Math.sin(value * 2 * Math.PI),
		y: Math.cos(value * 2 * Math.PI),
	};
}

function CompetenciesGraphLabel(props: {
	index: number;
	total: number;
	title: string;
	active?: boolean;
}) {
	const value = -props.index / props.total + 0.5;

	const { x, y } = fractionToXY(value);

	const spacing = 24;

	const width = (2 * Math.PI * (100 + spacing)) / props.total;
	const height = 32;

	return (
		<foreignObject
			width={width}
			height={height}
			transform={[
				...(props.active
					? []
					: [`translate(140, 140)`, `scale(0.75, 0.75)`, `translate(-140, -140)`]),
				`translate(${x * (100 + spacing) + 140}, ${y * (100 + spacing) + 140})`,
				`rotate(${(((1 - value) * 360 + 90) % 180) - 90})`,
				`translate(-${width / 2}, -16)`,
			].join(' ')}
			style={{
				transition: '.4s cubic-bezier(0, 1, 0, 1) transform',
			}}
		>
			<Label alignItems={(value + 0.75) % 1 > 0.5 ? 'flex-start' : 'flex-end'}>{props.title}</Label>
		</foreignObject>
	);
}

function CompetenciesGraphPiece(props: {
	index: number;
	total: number;
	levels: (boolean | null)[];
	active?: boolean;
}) {
	const theme = useTheme();

	const spacing = 4;

	const width = 80 / props.levels.length - spacing;

	const circles = props.levels.map((level, i) => (
		<circle
			key={i}
			cx="100"
			cy="100"
			r={20 + (80 / props.levels.length) * (i + 1) - width / 2}
			fill="none"
			stroke={
				theme.colors[level === true ? 'green' : level === false ? 'yellow' : 'lightBlueLight']
			}
			strokeWidth={width}
		/>
	));

	const startValue = ((props.total - props.index - 0.5) / props.total + 0.5) % 1;
	let endValue = ((props.total - props.index + 0.5) / props.total + 0.5) % 1;

	if (endValue <= startValue) {
		endValue += 1;
	}

	const start = fractionToXY(startValue);
	const end = fractionToXY(endValue);

	return (
		<g
			opacity={props.active ? 1 : 0.5}
			transform={props.active ? undefined : 'scale(0.75, 0.75)'}
			style={{
				transformOrigin: '100px 100px',
				transition: '.4s cubic-bezier(0, 1, 0, 1) transform',
			}}
		>
			<mask id={`frac${props.index}`}>
				<polygon
					points={[
						[0, 0],
						[start.x * 100, start.y * 100],
						Math.abs(start.x) > Math.abs(start.y)
							? [Math.round(start.x) * 100, start.y * 100]
							: [start.x * 100, Math.round(start.y) * 100],
						...[1, 2, 3, 4, 5, 6, 7, 8]
							.map((i) => i / 8)
							.filter((i) => startValue <= i && i <= endValue)
							.map((i) => {
								const { x, y } = fractionToXY(i);
								return [Math.round(x) * 100, Math.round(y) * 100];
							}),
						Math.abs(end.x) > Math.abs(end.y)
							? [Math.round(end.x) * 100, end.y * 100]
							: [end.x * 100, Math.round(end.y) * 100],
						[end.x * 100, end.y * 100],
					]
						.map(([x, y]) =>
							[(x / 100) * (100 + spacing) + 100, (y / 100) * (100 + spacing) + 100].join(','),
						)
						.join(' ')}
					fill="white"
					stroke="black"
					strokeWidth={spacing}
				/>
			</mask>
			<g mask={`url(#frac${props.index})`}>
				<rect x="0" y="0" width="200" height="200" fill="none" />
				{circles}
			</g>
		</g>
	);
}

export default function CompetenciesGraph(props: {
	data: { title: string; levels: (boolean | null)[] }[];
	selectedIndex?: number;
	onSelect: (index?: number) => void;
}) {
	const selectedIndex = props.selectedIndex;

	return (
		<svg
			width="336"
			height="336"
			style={{ cursor: 'pointer' }}
			onClick={(event) => {
				const { left, top, width, height } = event.currentTarget.getBoundingClientRect();
				const x = -1 + (2 * (event.clientX - left)) / width;
				const y = -1 + (2 * (event.clientY - top)) / height;
				const angle = (Math.atan2(y, x) + 0.5 * Math.PI) % (Math.PI * 2);
				const index = Math.round((angle / Math.PI / 2) * 7 + 7) % 7;

				if (index === props.selectedIndex) {
					props.onSelect(undefined);
				} else {
					props.onSelect(index);
				}
			}}
		>
			<g transform="scale(1.2, 1.2)">
				{props.data.map(({ title }, index) => (
					<CompetenciesGraphLabel
						key={index}
						index={index}
						total={props.data.length}
						title={title}
						active={selectedIndex === undefined || selectedIndex === index}
					/>
				))}
				<g transform="translate(40, 40)">
					{props.data.map(({ levels, title }, index) => (
						<CompetenciesGraphPiece
							key={title}
							index={index}
							total={props.data.length}
							levels={levels}
							active={selectedIndex === undefined || selectedIndex === index}
						/>
					))}
				</g>
			</g>
		</svg>
	);
}
